import { useState } from "react";
import { Tooltip } from "@mui/material";
import RFSStep1 from "../../../images/rfs-step-1.png";
import RFSStep2 from "../../../images/rfs-step-2.png";
import RFSStep2Advanced from "../../../images/rfs-step-2-advanced.png";
import RFSStep2Assign2 from "../../../images/rfs-step-2-assign2.png";
import RFSStep3 from "../../../images/rfs-step-3.png";
import ScantronSheetBlue from "../../../images/scantron-sheet-blue.png";
import ScantronSheetGreen from "../../../images/scantron-sheet-green.png";

/* ───────────────────────────────────────────────────────────────────────────
   IMAGE TOOLTIP COMPONENT
   ─────────────────────────────────────────────────────────────────────────── */

const ImageTooltip = ({ children, image, alt }) => {
	return (
		<Tooltip
			title={
				<img
					src={image}
					alt={alt}
					style={{
						width: "400px",
						height: "auto",
						display: "block",
					}}
				/>
			}
			arrow
			placement="top"
			componentsProps={{
				tooltip: {
					sx: {
						bgcolor: "white",
						border: "2px solid",
						borderColor: "primary.main",
						borderRadius: 2,
						p: 0.5,
						boxShadow: 3,
						"& .MuiTooltip-arrow": {
							color: "white",
							"&::before": {
								border: "1px solid",
								borderColor: "primary.main",
							},
						},
					},
				},
			}}
		>
			<span className="underline decoration-dotted decoration-primary/50 cursor-help text-primary font-medium">
				{children}
			</span>
		</Tooltip>
	);
};

/* ───────────────────────────────────────────────────────────────────────────
   DATA — add / reorder / remove items here without touching the JSX below.
   ─────────────────────────────────────────────────────────────────────────── */

/**
 * Top-level phases displayed as a vertical timeline.
 * Each phase has a title, a short blurb, and optional `details` (bullet list)
 * or `substeps` (interactive stepper with images).
 *
 * To add a new phase: push a new object.
 * To add a bullet: push into `details`.
 * To add a substep: push into `substeps`.
 */

// Step 2 content varies by exam type
const examTypeStep2Content = {
	"standard-mc": {
		title: "Complete the exam-specific fields",
		description:
			"For standard multiple choice, provide the number of questions (1-250), the number of answer options per question (A-E default), the lowest and highest question numbers, and the number of answer keys/versions.",
	},
	"multiple-response-mc": {
		title: "Complete the exam-specific fields",
		description: (
			<ul className="list-disc pl-5 space-y-1">
				<li>
					We need to collect additional information from you for any multiple choice exam that is to
					be graded as: MRScor (students select multiple answers for each question), Weighted
					(questions are worth different points), Formula Scoring, or any exam with an atypical
					design.
				</li>
				<li>
					Click the "Advanced Settings" button to open a window where you will be able to specify
					which of these grading types applies to your exam, and which questions to apply these
					advanced parameters to.
				</li>
				<li>
					After toggling the applicable advanced setting and configuring the number of questions and
					other general exam details, a grid of question items will populate for each answer key
					version.
				</li>
				<li>
					For MRScor or weighted exams, you can select the "tool" (toggle MR, set weight), the
					questions to apply the tool to, and then click "Apply". This step is extremely important,
					as your configuration here will determine how the exam is eventually graded.
				</li>
				<li>
					When you are finished configuring your answer key(s), click "Save & Close" and continue
					with the rest of the form.
				</li>
			</ul>
		),
	},
	"assign2-written": {
		title: "Fill out the exam-specific fields and submit your request",
		description:
			"For written (Assign2) exam scanning, we require information regarding: number of booklets and sheets, the class, the drop off date/time, and whether you would like TSQS to send you the scanned file or upload it to Canvas on your behalf.",
		warning:
			"For Assign2 written exams, please submit your request as early as possible (as soon as you know the expected drop off date is preferred) so that we can ensure timely processing of all exams.",
	},
};

// Step 3 content varies by exam type
const examTypeStep3Content = {
	"standard-mc": {
		title: "Select your class and submit your request",
		description: (
			<ul className="list-disc pl-5 space-y-1">
				<li>
					Search for and select your class from the list. If you cannot find your class, please
					contact TSQS for assistance.
				</li>
				<li>
					Review your information, add any necessary additional comments for grading, and click
					Submit. A confirmation receipt will be automatically downloaded to your computer.
				</li>
				<li>
					Please include this receipt with your package when you drop off your exams, or if you do
					not have access to a printer you can write the requisition number in a visible spot on
					your package.
				</li>
			</ul>
		),
	},
	"multiple-response-mc": {
		title: "Select your class and submit your request",
		description: (
			<ul className="list-disc pl-5 space-y-1">
				<li>
					Search for and select your class from the list. If you cannot find your class, please
					contact TSQS for assistance.
				</li>
				<li>
					Review your information, add any necessary additional comments for grading, and click
					Submit. A confirmation receipt will be automatically downloaded to your computer.
				</li>
				<li>
					Please include this receipt with your package when you drop off your exams, or if you do
					not have access to a printer you can write the requisition number in a visible spot on
					your package.
				</li>
			</ul>
		),
	},
	"assign2-written": {
		title: "After you submit",
		description:
			"If you have chosen to have TSQS upload the file on your behalf, please reach out to us so that we can coordinate the correct team member to be added as an instructor to your Canvas course. If you have chosen to receive the scanned file via email, we will send it to the email address you provided on your request form once scanning is complete.",
	},
};

const examTypeStep2Image = {
	"standard-mc": {
		image: RFSStep2,
	},
	"multiple-response-mc": {
		image: RFSStep2Advanced,
	},
	"assign2-written": {
		image: RFSStep2Assign2,
	},
};

const examTypeStep3Image = {
	"standard-mc": {
		image: RFSStep3,
	},
	"multiple-response-mc": {
		image: RFSStep3,
	},
	"assign2-written": {
		image: null,
	},
};

const phases = [
	{
		title: "Prepare your exam",
		blurb: "Instructions for preparing your exam materials.",
		details: [
			<div>
				Create your exam and bubble in an answer key on a scantron sheet.{" "}
				<strong>A physical key is mandatory</strong>, emailed or scanned copies are not accepted.
			</div>,
			<div>
				We accept both standard{" "}
				<ImageTooltip image={ScantronSheetBlue} alt="Blue scantron sheet">
					blue
				</ImageTooltip>{" "}
				and{" "}
				<ImageTooltip image={ScantronSheetGreen} alt="Green scantron sheet">
					green
				</ImageTooltip>{" "}
				scantron sheets. TSQS <strong>cannot</strong> provide instructors with more than 50 blank
				scantron sheets at a time, and only in emergency circumstances.{" "}
				<strong>
					Scantron sheets must be sourced from your own department or purchased from the University
					Bookstore.
				</strong>
			</div>,
			`If the exam has multiple versions, assign each key a distinct special code in column J ("Special Codes") onward. Special codes can be any number (e.g. "1" for version 1 and "111111" for version 1 both work the same), so long as they are unique for each key. Students must also bubble in their special codes for their exam version and this must match the corresponding answer key.`,
			"Identifying info or a name on the answer key is optional — all that matters is that it is clearly identifiable as (one of) the answer key(s). Some instructors like to write their name and class title on their answer keys in pen (as pen ink does not register with the scanner).",
		],
	},
	{
		title: "Submit a request online",
		blurb: "Use the request form to tell us about your exam so we can calibrate our scanner.",
		substeps: true, // Flag to render the dynamic exam type selector
	},
	{
		title: "Deliver your exams",
		blurb: "Get your exam package to our office so we can begin scanning.",
		details: [
			"Drop off your exam package (scantron sheets + physical answer key(s) + confirmation receipt/requisition number) at our office, or send it through campus mail.",
			"Select departments may also use the courier service. If this option is available to you, it will appear in the request form.",
		],
	},
	{
		title: "Scanning & results",
		blurb: "What to expect:",
		details: [
			"Our standard turnaround time is 1 working day. Rare exceptions may apply for particularly complex exams, but we will let you know ahead of time if this applies to your exam.",
			"Once scanning is complete, we will email your results to the email address(es) you provided on your request form.",
			"Your scantron sheets will be returned based on the method you selected when you submitted the request: held at our office for pick-up, sent back through campus mail, or via courier for select departments.",
		],
	},
];

/* ───────────────────────────────────────────────────────────────────────────
   ICONS (small inline SVGs so we don't need an icon library)
   ─────────────────────────────────────────────────────────────────────────── */

const CheckIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		className="w-4 h-4"
		fill="none"
		viewBox="0 0 24 24"
		stroke="currentColor"
		strokeWidth={2.5}
	>
		<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
	</svg>
);

const numberToLetterMap = {
	1: "A",
	2: "B",
	3: "C",
};

/* ───────────────────────────────────────────────────────────────────────────
   SUB-COMPONENTS
   ─────────────────────────────────────────────────────────────────────────── */

/** Interactive stepper for the online request substeps (with screenshots). */
function RequestSubsteps({ examType, setExamType, examTypes }) {
	const [active, setActive] = useState(0);

	// Build substeps based on exam type - step 2 varies, steps 1 and 3 are constant
	const substeps = [
		{
			title: "Enter instructor information and choose type of exam",
			description:
				"Fill in your name, CCID, department, email, and delivery preference. All fields marked with an asterisk (*) are required.",
			image: RFSStep1,
		},
		{
			...examTypeStep2Content[examType],
			image: examTypeStep2Image[examType].image,
		},
		{
			...examTypeStep3Content[examType],
			image: examTypeStep3Image[examType].image,
		},
	];

	return (
		<div className="mt-4">
			{/* Pill tabs */}
			<div className="flex items-center gap-2 mb-4 overflow-x-auto justify-center 2xl:justify-between">
				{substeps.map((s, i) => (
					<>
						<button
							key={i}
							onClick={() => setActive(i)}
							className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition
							${active === i ? "bg-primary text-white shadow" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
						>
							<span className=" inline md:order-1 2xl:hidden">Step</span>

							<span
								className={`inline-flex items-center order-2 2xl:order-1 justify-center w-5 h-5 rounded-full text-[0.65rem] font-bold
							${active === i ? "bg-white text-primary" : "bg-gray-300 text-white"}`}
							>
								{i + 1 > 3 ? i + 1 : numberToLetterMap[i + 1]}
							</span>
							<span className="hidden 2xl:inline 2xl:order-2">{s.title}</span>
						</button>
						{i < substeps.length - 1 && (
							<div className="h-0.5 w-12 2xl:w-20 bg-gray-300 hidden md:block" />
						)}
					</>
				))}
			</div>

			{/* Detail card */}
			<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
				{active !== 0 && (
					<div className="mb-4 p-4  rounded-lg flex md:flex-row flex-col items-center  gap-2">
						<label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
							Exam Type
						</label>
						<select
							value={examType}
							onChange={(e) => setExamType(e.target.value)}
							className="p-2 border border-gray-300 rounded w-full max-w-lg"
						>
							{examTypes.map((type) => (
								<option key={type.id} value={type.id}>
									{type.label}
								</option>
							))}
						</select>
					</div>
				)}
				<div className="flex items-start gap-3">
					<span className="inline-flex shrink-0 items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-sm font-bold">
						{active + 1 > 3 ? active + 1 : numberToLetterMap[active + 1]}
					</span>
					<div>
						<h4 className="text-sm font-semibold text-gray-800 mb-1">{substeps[active].title}</h4>
						<div className="text-gray-600 text-sm leading-relaxed">
							{substeps[active].description}
						</div>

						{/* Exam type selector - shown in step 1 */}

						{substeps[active].warning && (
							<div className="mt-3 p-3 bg-red-50 border border-red-300 rounded-lg text-red-800 text-xs leading-relaxed">
								⚠️ <strong>Important:</strong> {substeps[active].warning}
							</div>
						)}
						{substeps[active].image && (
							<img
								src={substeps[active].image}
								alt={`Step ${active + 1} screenshot`}
								className="mt-3  border border-gray-200 rounded bg-gray-400 p-4 w-full mx-auto h-auto"
							/>
						)}
					</div>
				</div>

				{/* Prev / Next */}
				<div className="flex justify-between mt-4 pt-3 border-t border-gray-200">
					<button
						disabled={active === 0}
						onClick={() => setActive((s) => s - 1)}
						className="px-3 py-1.5 text-lg font-medium rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed text-primary hover:bg-primary/5"
					>
						← Previous
					</button>
					<button
						disabled={active === substeps.length - 1}
						onClick={() => setActive((s) => s + 1)}
						className="px-3 py-1.5 text-lg font-medium rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed text-primary hover:bg-primary/5"
					>
						Next →
					</button>
				</div>
			</div>
		</div>
	);
}

/* ───────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ─────────────────────────────────────────────────────────────────────────── */

export default function TestScoringGuide() {
	const [examType, setExamType] = useState("standard-mc");

	const examTypes = [
		{ id: "standard-mc", label: "Standard (Single Answer) Multiple Choice" },
		{
			id: "multiple-response-mc",
			label: "Advanced (MRScor, Weighted, Non-Contiguous) Multiple Choice",
		},
		{ id: "assign2-written", label: "Written (Assign2)" },
	];

	return (
		<div className="flex flex-col gap-10">
			{/* ── Process overview (vertical timeline) ────────────────── */}
			<section>
				<h2 className="text-2xl font-semibold text-primary mb-1">Exam Scanning Information</h2>
				<p className="text-gray-500 text-sm mb-6">
					Below is an up-to-date guide on how to prepare and submit your exam for scanning, as well
					as what to expect after submission. Click through the steps to see detailed instructions
					and screenshots for each part of the process. If you have any questions or need assistance
					at any point, please don't hesitate to contact TSQS.
				</p>

				<div className="relative pl-8 flex flex-col gap-6">
					{/* Vertical line */}
					<div className="absolute left-[5px] top-2 bottom-2 w-0.5 bg-gray-200" />

					{phases.map((phase, i) => (
						<div key={i} className="relative">
							{/* Timeline dot */}
							<span className="absolute -left-10 top-0.5 inline-flex items-center justify-center w-[30px] h-[30px] rounded-full bg-primary text-white text-sm font-bold ring-4 ring-white">
								{i + 1}
							</span>

							<div>
								<h3 className="text-lg font-semibold text-gray-800">{phase.title}</h3>
								<p className="text-gray-500 text-sm mt-0.5 mb-3">{phase.blurb}</p>

								{/* Bullet details */}
								{phase.details && (
									<ul className="flex flex-col gap-2">
										{phase.details.map((d, j) => (
											<li
												key={j}
												className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed"
											>
												<span className="mt-0.5 inline-flex shrink-0 items-center justify-center w-5 h-5 rounded bg-primary/10 text-primary">
													<CheckIcon />
												</span>
												{d}
											</li>
										))}
									</ul>
								)}

								{/* Interactive substeps */}
								{phase.substeps && (
									<RequestSubsteps
										examType={examType}
										setExamType={setExamType}
										examTypes={examTypes}
									/>
								)}
							</div>
						</div>
					))}
				</div>
			</section>
		</div>
	);
}
